// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { formatStateOrStatus, getID, getNumericID } from '../../utils';
import { formatNumber } from '../../utils/formatNumber';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import OrderIdLink from './OrderIdLink';
import OrderOptions from './OrderOptions';

interface OrderData {
  readonly id: string;
  readonly order_fulfillment_time: number;
  readonly order_status: string;
  readonly customer: string;
  readonly trays?: number; // For tray breakdown in planting group view
}
interface Props {
  readonly orders: OrderData[];
  readonly showOptions?: boolean;
  readonly showCustomer?: boolean;
  readonly showTrays?: boolean;
}

dayjs.extend(relativeTime);
function getTimeFromNow(time: number, timezone: string) {
  const timeIn = dayjs.unix(time).tz(timezone);
  const now = dayjs().tz(timezone);
  // If the time is tomorrow, then return "Tomorrow"
  if (timeIn.isSame(now.add(1, 'day'), 'day')) {
    return 'Tomorrow';
  }
  // If the time is yesterday, then return "Yesterday"
  if (timeIn.isSame(now.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }
  // If the time is today, then return "Today"
  if (timeIn.isSame(now, 'day')) {
    return 'Today';
  }

  return timeIn.fromNow();
}

export default function OrderTable({ orders, showOptions, showCustomer = true, showTrays = false }: Props) {
  const { permissions, timezone, language } = useUserContext();
  const userCanEditOrders = permissions.can_edit_orders;
  const idValues: React.ReactElement[] = [];
  const orderStatusValues: string[] = [];
  const customerValues: string[] = [];
  const fulfillmentTimeValues: React.ReactElement[] = [];
  const timeFromNowValues: string[] = [];
  const traysValues: string[] = [];
  for (const order of orders) {
    const gotId = getID(order.id);
    idValues.push(<OrderIdLink id={gotId} />);
    orderStatusValues.push(formatStateOrStatus(order.order_status));
    customerValues.push(order.customer);
    timeFromNowValues.push(getTimeFromNow(order.order_fulfillment_time, timezone));
    fulfillmentTimeValues.push(
      <VisibleAtContainer
        up={<Time time={order.order_fulfillment_time} />}
        down={<Time time={order.order_fulfillment_time} options={{ compactDisplay: true }} />}
      />,
    );
    traysValues.push(order.trays !== undefined ? formatNumber(order.trays, language) : '');
  }
  const columns: ColumnType[] = [
    {
      name: 'Order ID',
      smallName: 'ID',
      values: idValues,
    },
    {
      name: 'Status',
      values: orderStatusValues,
      visibleAt: 'xs' as const,
    },
    showTrays
      ? {
          name: 'Trays',
          values: traysValues,
          numeric: true,
        }
      : null,
    showCustomer
      ? {
          name: 'Customer',
          values: customerValues,
        }
      : null,
    {
      name: 'Delivery Date',
      values: fulfillmentTimeValues,
    },
    {
      name: 'Time To',
      values: timeFromNowValues,
      visibleAt: 'sm' as const,
    },
  ].filter(utils.notNull);
  if (showOptions && userCanEditOrders) {
    columns.push({
      name: 'Actions',
      smallName: '',
      numeric: true,
      flex: '0 0 auto',
      values: orders.map((order) => (
        <OrderOptions
          key={order.id}
          onSuccess={() => {
            /** do nothing */
          }}
          orderStatus={order.order_status}
          useMoreIcon
          orderId={getNumericID(order.id)}
          orderFulfillmentTime={order.order_fulfillment_time}
        />
      )),
    });
  }

  return <Table columns={columns} />;
}
